import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import API from '../../../services/API'
import useIsMobile from '../../../helpers/useIsMobile'
import {
  DOPS_DOMAINS,
  DOPS_OVERALL,
  DOPS_RATINGS,
} from '../ePCR/liveCapture/catalogue'
import { apiError } from './dopsShared'

// Short labels for the segmented buttons on narrow screens
const SHORT_RATING = {
  'Below expectations': 'Below',
  Borderline: 'Borderline',
  'Meets expectations': 'Meets',
  'Above expectations': 'Above',
  'Not observed': 'N/O',
}

// Assessor's structured DOPS: each domain rated, an overall judgement and
// free-text feedback. POST /dops/:id/complete (or /decline).
const DopsAssessmentForm = ({ dops, onSaved }) => {
  const isMobile = useIsMobile()
  const [ratings, setRatings] = useState({})
  const [overall, setOverall] = useState('')
  const [strengths, setStrengths] = useState('')
  const [development, setDevelopment] = useState('')
  const [agreedActions, setAgreedActions] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [declineOpen, setDeclineOpen] = useState(false)
  const [declineReason, setDeclineReason] = useState('')

  const rated = DOPS_DOMAINS.filter((domain) => ratings[domain]).length

  const complete = () => {
    setError('')
    if (!rated) {
      setError('Rate at least one domain (use "Not observed" where it applies).')
      return
    }
    if (!overall) {
      setError('Choose an overall judgement.')
      return
    }
    setBusy(true)
    API.post(`/dops/${dops._id}/complete`, {
      ratings: DOPS_DOMAINS.filter((domain) => ratings[domain]).map((domain) => ({
        domain,
        rating: ratings[domain],
      })),
      overall,
      strengths: strengths.trim(),
      development: development.trim(),
      agreedActions: agreedActions.trim(),
    })
      .then((res) => {
        if (res.status === 200) onSaved?.('completed')
        else setError(apiError(res, 'Unable to complete this DOPS'))
      })
      .catch(() => setError('Unable to reach AmbuNet. Please try again.'))
      .finally(() => setBusy(false))
  }

  const decline = () => {
    setBusy(true)
    setError('')
    API.post(`/dops/${dops._id}/decline`, { reason: declineReason.trim() })
      .then((res) => {
        if (res.status === 200) {
          setDeclineOpen(false)
          onSaved?.('declined')
        } else {
          setError(apiError(res, 'Unable to decline this DOPS'))
          setDeclineOpen(false)
        }
      })
      .catch(() => setError('Unable to reach AmbuNet. Please try again.'))
      .finally(() => setBusy(false))
  }

  return (
    <Stack gap={2}>
      <Box>
        <Typography fontWeight={800}>Rate each domain</Typography>
        <Typography variant="caption" color="text.secondary">
          {rated} of {DOPS_DOMAINS.length} rated · use &quot;Not observed&quot;
          where a domain didn&apos;t apply
        </Typography>
      </Box>
      <Stack
        sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
      >
        {DOPS_DOMAINS.map((domain, index) => (
          <Stack
            key={domain}
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            gap={1}
            sx={{
              px: 1.5,
              py: 1,
              borderTop: index ? 1 : 0,
              borderColor: 'divider',
              bgcolor: ratings[domain] ? 'transparent' : 'action.hover',
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
              {domain}
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={ratings[domain] ?? null}
              onChange={(e, value) =>
                setRatings((prev) => ({ ...prev, [domain]: value ?? undefined }))
              }
              sx={{
                flexShrink: 0,
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  px: 1,
                  py: 0.4,
                  fontSize: '0.75rem',
                  flex: isMobile ? '1 0 auto' : undefined,
                },
              }}
            >
              {DOPS_RATINGS.map((rating) => (
                <ToggleButton key={rating} value={rating} title={rating}>
                  {SHORT_RATING[rating] ?? rating}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        ))}
      </Stack>

      <FormControl>
        <Typography fontWeight={800}>Overall judgement</Typography>
        <RadioGroup value={overall} onChange={(e) => setOverall(e.target.value)}>
          {DOPS_OVERALL.map((option) => (
            <FormControlLabel key={option} value={option} control={<Radio size="small" />} label={option} />
          ))}
        </RadioGroup>
      </FormControl>

      <TextField
        label="Strengths"
        multiline
        minRows={2}
        size="small"
        value={strengths}
        onChange={(e) => setStrengths(e.target.value)}
      />
      <TextField
        label="Development points"
        multiline
        minRows={2}
        size="small"
        value={development}
        onChange={(e) => setDevelopment(e.target.value)}
      />
      <TextField
        label="Agreed actions"
        multiline
        minRows={2}
        size="small"
        value={agreedActions}
        onChange={(e) => setAgreedActions(e.target.value)}
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
        <Button color="error" onClick={() => setDeclineOpen(true)} disabled={busy}>
          Decline
        </Button>
        <Button
          variant="contained"
          onClick={complete}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} /> : null}
        >
          Complete DOPS
        </Button>
      </Stack>

      <Dialog open={declineOpen} onClose={busy ? undefined : () => setDeclineOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Decline this DOPS?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            The clinician will be told you declined. Let them know why, e.g.
            you didn&apos;t observe the procedure.
          </Typography>
          <TextField
            label="Reason"
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeclineOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={decline} disabled={busy}>
            Decline
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default DopsAssessmentForm
